import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { Overview } from '../pages/Overview';
import { People } from '../pages/People';
import { GHGInventory } from '../pages/GHGInventory';
import { Energy } from '../pages/Energy';
import { Transportation } from '../pages/Transportation';
import { PolicyTool } from '../pages/PolicyTool';
import { Community } from '../pages/Community';
import { Other } from '../pages/Other';
import AdminSettingsPage from '../pages/AdminSettingsPage';
import { CalculatorPage } from '../pages/CalculatorPage';
import { PublicationAnalysisPage } from '../pages/PublicationAnalysisPage';

const SECTION_IDS = [
  'overview',
  'people',
  'ghg',
  'buildings',
  'transportation',
  'policy-tool',
  'community',
  'other',
  'admin-settings',
  'coolclimate-calculator',
  'analysis',
] as const;

type SectionId = (typeof SECTION_IDS)[number];

function getSectionFromPath(pathname: string, locationSlug: string): SectionId {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0 || parts[0] !== locationSlug) return 'overview';
  const segment = parts[1];
  if (!segment || segment === 'overview') return 'overview';
  if (SECTION_IDS.includes(segment as SectionId)) return segment as SectionId;
  return 'overview';
}

const MAX_RETAINED_SECTIONS = 2;

export interface LocationShellProps {
  onLocationSelect: (location: string) => void;
}

export const LocationShell: React.FC<LocationShellProps> = ({ onLocationSelect }) => {
  const { locationSlug } = useParams<{ locationSlug: string }>();
  const { pathname } = useLocation();
  const { selectedLocation, locationData } = useLocationContext();
  const catalogSize = locationData?.availableLocations?.length ?? 0;
  // LRU list: most-recent first, capped to MAX_RETAINED_SECTIONS
  const [retainedSections, setRetainedSections] = useState<SectionId[]>(['overview']);
  const prevPathRef = useRef<string>('');
  const prevSlugRef = useRef<string>('');

  // Use peek* until location service finishes initialize() — avoids console noise and wrong first paint.
  const resolvedLocation = useMemo(() => {
    if (!locationSlug) return selectedLocation;
    void catalogSize;
    const fromSlug = supabaseLocationService.peekLocationBySlug(locationSlug);
    if (!selectedLocation) return fromSlug?.name ?? '';
    return fromSlug?.name ?? selectedLocation;
  }, [locationSlug, selectedLocation, catalogSize]);

  const currentSection = getSectionFromPath(pathname, locationSlug ?? '');

  useEffect(() => {
    if (!locationSlug) return;
    if (prevSlugRef.current !== locationSlug) {
      prevSlugRef.current = locationSlug;
      prevPathRef.current = pathname;
      setRetainedSections([currentSection]);
      return;
    }
    if (prevPathRef.current !== pathname) {
      prevPathRef.current = pathname;
      setRetainedSections((prev) => {
        const without = prev.filter((s) => s !== currentSection);
        return [currentSection, ...without].slice(0, MAX_RETAINED_SECTIONS);
      });
    }
  }, [locationSlug, pathname, currentSection]);

  useEffect(() => {
    if (locationSlug && prevSlugRef.current === '') prevSlugRef.current = locationSlug;
  }, [locationSlug]);

  if (!locationSlug) return null;

  const sectionContent: Record<SectionId, React.ReactNode> = {
    overview: (
      <Overview onLocationSelect={onLocationSelect} isLandingPage={false} />
    ),
    people: <People location={resolvedLocation} />,
    ghg: <GHGInventory location={resolvedLocation} />,
    buildings: <Energy location={resolvedLocation} />,
    transportation: <Transportation location={resolvedLocation} />,
    'policy-tool': <PolicyTool />,
    community: <Community />,
    other: <Other />,
    'admin-settings': <AdminSettingsPage />,
    'coolclimate-calculator': <CalculatorPage />,
    analysis: <PublicationAnalysisPage location={resolvedLocation} />,
  };

  return (
    <>
      {retainedSections.map((sectionId) => {
        const isActive = currentSection === sectionId;
        return (
          <div
            key={sectionId}
            style={{ display: isActive ? 'block' : 'none' }}
            aria-hidden={!isActive}
          >
            {sectionContent[sectionId]}
          </div>
        );
      })}
    </>
  );
};
